/* metadata-scrub.js — Timbrica metadata scrubber engine.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS (root fix, not symptoms)
 * ═══════════════════════════════════════════════════════════════════════════
 * The obvious way to strip metadata is a BLOCKLIST: "delete the EXIF segment,
 * delete XMP, delete IPTC". Every shipped stripper we measured works that way,
 * including our own image-metadata-engine.js. It loses, and it loses silently,
 * because a blocklist only knows yesterday's metadata containers. Measured on
 * real files (tests/scratch fixtures, 2026-07-30):
 *
 *   • C2PA / Content Credentials ride in JPEG APP11 (JUMBF). Not on the list →
 *     the signed provenance manifest survived a "remove all metadata" run.
 *   • Samsung/Google Motion Photos append a WHOLE MP4 (video + audio of the
 *     room) AFTER the JPEG's EOI marker. Not a segment at all → survived.
 *   • MPF (APP2) appends extra full-size JPEGs the same way.
 *   • HEIC/AVIF/GIF/TIFF weren't handled → threw, nothing removed. HEIC is the
 *     iPhone default, i.e. the single most common "remove my GPS" input.
 *
 * So this engine inverts the rule. It is an ALLOWLIST: keep only the bytes a
 * decoder needs to render the image correctly, drop everything else, and stop
 * at the end-of-image marker. Unknown vendor segment invented next year? Gone
 * by default, without a code change. That is the difference between fixing the
 * class of bug and fixing the instances.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE TWO THINGS AN ALLOWLIST MUST NOT BREAK
 * ═══════════════════════════════════════════════════════════════════════════
 * 1. ORIENTATION. EXIF Orientation is metadata, but it is also the only record
 *    that a phone held the camera sideways. Strip it wholesale and every
 *    portrait photo lands 90° over — measured: 480x640 → 640x480. We keep a
 *    MINIMAL 26-byte EXIF block carrying Orientation alone (a number 1..8,
 *    which identifies nobody), or physically rotate if the caller prefers.
 * 2. COLOR. ICC profiles (APP2/iCCP/colr) and the APP14 Adobe transform marker
 *    are metadata by segment type but are load-bearing for correct color. Wide
 *    gamut photos go flat and CMYK JPEGs invert without them. Kept by default.
 *
 * Everything here is byte surgery on the ENCODED file. Nothing is decoded and
 * nothing is re-encoded, so pixels are bit-exact and the operation costs
 * microseconds — which is why the whole thing runs in the browser with no
 * WASM, no upload, and no practical batch ceiling.
 *
 * Exposes `window.MetaScrub`; also CommonJS-exported so the identical file is
 * what the Node tests run (tests/scratch/metadata-scrub-*.mjs).
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.MetaScrub = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* ════════════════════════════════════════════════════════════════════
     * METADATA CLASSES — the vocabulary the UI and the report speak.
     * Each is independently keepable, because "remove metadata" means very
     * different things to a person hiding their home address and to a
     * photographer publishing to a portfolio.
     * ════════════════════════════════════════════════════════════════════ */
    var CLASSES = ['gps', 'datetime', 'camera', 'serial', 'settings', 'person', 'copyright',
                   'thumbnail', 'c2pa', 'hidden', 'icc', 'other'];

    // Default: remove personal + provenance + hidden payloads; keep the two
    // load-bearing things (color, orientation) and nothing else.
    function defaults(opts) {
        var o = opts || {};
        var keep = o.keep || {};
        return {
            keep: {
                gps: !!keep.gps,
                datetime: !!keep.datetime,
                camera: !!keep.camera,
                serial: !!keep.serial,
                settings: !!keep.settings,
                person: !!keep.person,
                copyright: !!keep.copyright,
                thumbnail: !!keep.thumbnail,
                c2pa: !!keep.c2pa,
                hidden: !!keep.hidden,
                icc: keep.icc !== false,          // color fidelity — kept unless asked
                other: !!keep.other,
            },
            keepOrientation: o.keepOrientation !== false,
        };
    }

    // Named presets. `custom` just means "caller passed explicit keep flags".
    var PRESETS = {
        // Everything identifying goes. Color + orientation stay.
        full:     { gps: 0, datetime: 0, camera: 0, serial: 0, settings: 0, person: 0, copyright: 0, thumbnail: 0, c2pa: 0, hidden: 0, icc: 1, other: 0 },
        // Only the location. A holiday photo keeps its date and camera.
        location: { gps: 0, datetime: 1, camera: 1, serial: 1, settings: 1, person: 1, copyright: 1, thumbnail: 1, c2pa: 1, hidden: 1, icc: 1, other: 1 },
        // Publishing preset: the photographer keeps credit + shooting data,
        // loses their home coordinates, serial numbers and private notes.
        // `serial` is split from `camera` on purpose — "Canon EOS R5" is a
        // camera MODEL that thousands of people own, while BodySerialNumber is
        // a unique key that ties every photo you ever posted to one body (and
        // to the receipt with your name on it). Keeping the model while
        // dropping the serial is the distinction a portfolio actually wants.
        share:    { gps: 0, datetime: 1, camera: 1, serial: 0, settings: 1, person: 0, copyright: 1, thumbnail: 0, c2pa: 0, hidden: 0, icc: 1, other: 0 },
    };
    function presetKeep(name) {
        var p = PRESETS[name] || PRESETS.full, k = {};
        for (var c in p) if (Object.prototype.hasOwnProperty.call(p, c)) k[c] = !!p[c];
        return k;
    }

    /* ════════════════════════════════════════════════════════════════════
     * BYTE HELPERS
     * ════════════════════════════════════════════════════════════════════ */
    function u8of(x) { return x instanceof Uint8Array ? x : new Uint8Array(x); }
    /* ALWAYS a private copy. `.slice()` is NOT safe for this: on a Node Buffer
     * it returns an aliasing view (Buffer.slice === subarray), so writing to
     * the "copy" silently corrupts the caller's input. Measured 2026-07-30 —
     * the scrubber was quietly damaging the very file it was handed, which the
     * browser hid because ArrayBuffer-derived Uint8Arrays copy on slice(). */
    function copyOf(u8, start, end) {
        var out = new Uint8Array(end - start);
        out.set(u8.subarray(start, end));
        return out;
    }
    function ascii(u8, off, len) {
        var s = '';
        for (var i = 0; i < len && off + i < u8.length; i++) s += String.fromCharCode(u8[off + i]);
        return s;
    }
    function concat(parts) {
        var total = 0, i;
        for (i = 0; i < parts.length; i++) total += parts[i].length;
        var out = new Uint8Array(total), pos = 0;
        for (i = 0; i < parts.length; i++) { out.set(parts[i], pos); pos += parts[i].length; }
        return out;
    }
    function slices(u8, ranges) {
        var total = 0, i;
        for (i = 0; i < ranges.length; i++) total += ranges[i][1] - ranges[i][0];
        var out = new Uint8Array(total), pos = 0;
        for (i = 0; i < ranges.length; i++) {
            out.set(u8.subarray(ranges[i][0], ranges[i][1]), pos);
            pos += ranges[i][1] - ranges[i][0];
        }
        return out;
    }
    function rd(u8, off, n, le) {
        var v = 0;
        if (le) { for (var i = n - 1; i >= 0; i--) v = v * 256 + u8[off + i]; }
        else { for (var j = 0; j < n; j++) v = v * 256 + u8[off + j]; }
        return v;
    }
    function wr(u8, off, n, val, le) {
        for (var i = 0; i < n; i++) {
            var shift = le ? i : (n - 1 - i);
            u8[off + i] = Math.floor(val / Math.pow(256, shift)) & 0xff;
        }
    }
    function utf8(s) {
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s);
        var u = unescape(encodeURIComponent(s)), a = new Uint8Array(u.length);
        for (var i = 0; i < u.length; i++) a[i] = u.charCodeAt(i);
        return a;
    }

    /* ════════════════════════════════════════════════════════════════════
     * FORMAT DETECTION (magic bytes — never the file extension)
     * ════════════════════════════════════════════════════════════════════ */
    function detect(u8) {
        if (!u8 || u8.length < 12) return 'unknown';
        var b = u8;
        if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpeg';
        if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'png';
        if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP') return 'webp';
        if (ascii(b, 0, 3) === 'GIF') return 'gif';
        if ((b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2A && b[3] === 0x00) ||
            (b[0] === 0x4D && b[1] === 0x4D && b[2] === 0x00 && b[3] === 0x2A)) return 'tiff';
        if (b[0] === 0x42 && b[1] === 0x4D) return 'bmp';
        if (ascii(b, 4, 4) === 'ftyp') {
            var brand = ascii(b, 8, 4).toLowerCase();
            if (brand.indexOf('avi') === 0) return 'avif';
            return 'heic';   // heic / heix / hevc / mif1 / msf1 / heim …
        }
        return 'unknown';
    }

    /* ════════════════════════════════════════════════════════════════════
     * TIFF / EXIF IFD CODEC
     * ────────────────────────────────────────────────────────────────────
     * One codec serves five containers: the JPEG APP1 payload, the PNG eXIf
     * chunk, the WebP EXIF chunk, the HEIC/AVIF `Exif` item and actual .tif
     * files. Writing it once is the reason selective removal ("drop GPS, keep
     * the camera") behaves identically everywhere instead of being a
     * JPEG-only feature like it is in every tool we compared.
     * ════════════════════════════════════════════════════════════════════ */
    var TYPE_SIZE = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8, 13: 4, 16: 8, 17: 8, 18: 8 };

    var T = {
        ImageDescription: 0x010E, Make: 0x010F, Model: 0x0110, Orientation: 0x0112,
        XResolution: 0x011A, YResolution: 0x011B, ResolutionUnit: 0x0128,
        Software: 0x0131, DateTime: 0x0132, Artist: 0x013B, HostComputer: 0x013C,
        WhitePoint: 0x013E, PrimaryChromaticities: 0x013F,
        JPEGInterchangeFormat: 0x0201, JPEGInterchangeFormatLength: 0x0202,
        YCbCrCoefficients: 0x0211, YCbCrPositioning: 0x0213, ReferenceBlackWhite: 0x0214,
        XMLPacket: 0x02BC, IPTC: 0x83BB, ICCProfile: 0x8773,
        Copyright: 0x8298, ExifIFD: 0x8769, GPSIFD: 0x8825, InteropIFD: 0xA005,
        // Windows XP fields (UCS-2, written by Explorer's "Details" tab)
        XPTitle: 0x9C9B, XPComment: 0x9C9C, XPAuthor: 0x9C9D, XPKeywords: 0x9C9E, XPSubject: 0x9C9F,
        // Exif IFD
        DateTimeOriginal: 0x9003, DateTimeDigitized: 0x9004,
        OffsetTime: 0x9010, OffsetTimeOriginal: 0x9011, OffsetTimeDigitized: 0x9012,
        SubSecTime: 0x9290, SubSecTimeOriginal: 0x9291, SubSecTimeDigitized: 0x9292,
        MakerNote: 0x927C, UserComment: 0x9286, ImageUniqueID: 0xA420,
        CameraOwnerName: 0xA430, BodySerialNumber: 0xA431,
        LensSpecification: 0xA432, LensMake: 0xA433, LensModel: 0xA434, LensSerialNumber: 0xA435,
        RelatedSoundFile: 0xA004,
        // structural, needed to interpret the pixels of a real .tif
        StripOffsets: 0x0111, StripByteCounts: 0x0117, TileOffsets: 0x0144, TileByteCounts: 0x0145,
        FreeOffsets: 0x0120, FreeByteCounts: 0x0121,
    };

    // tag → metadata class. Anything absent from this map is treated as
    // `settings` inside the Exif IFD (exposure, ISO, aperture: technical, not
    // identifying) and as `other` in IFD0.
    var TAG_CLASS = {};
    (function () {
        var m = TAG_CLASS;
        m[T.Make] = 'camera'; m[T.Model] = 'camera'; m[T.Software] = 'camera';
        m[T.HostComputer] = 'camera'; m[T.LensMake] = 'camera'; m[T.LensModel] = 'camera';
        m[T.LensSerialNumber] = 'serial'; m[T.BodySerialNumber] = 'serial';
        m[T.MakerNote] = 'serial';        // vendor blob: serials, sometimes GPS
        m[T.ImageUniqueID] = 'serial';
        m[T.DateTime] = 'datetime'; m[T.DateTimeOriginal] = 'datetime'; m[T.DateTimeDigitized] = 'datetime';
        m[T.OffsetTime] = 'datetime'; m[T.OffsetTimeOriginal] = 'datetime'; m[T.OffsetTimeDigitized] = 'datetime';
        m[T.SubSecTime] = 'datetime'; m[T.SubSecTimeOriginal] = 'datetime'; m[T.SubSecTimeDigitized] = 'datetime';
        m[T.Artist] = 'person'; m[T.ImageDescription] = 'person'; m[T.UserComment] = 'person';
        m[T.CameraOwnerName] = 'person'; m[T.RelatedSoundFile] = 'person';
        m[T.XPTitle] = 'person'; m[T.XPComment] = 'person'; m[T.XPAuthor] = 'person';
        m[T.XPKeywords] = 'person'; m[T.XPSubject] = 'person';
        m[T.XMLPacket] = 'person';        // XMP embedded in TIFF
        m[T.IPTC] = 'person';
        m[T.Copyright] = 'copyright';
        m[T.ICCProfile] = 'icc';
    })();

    // Tags a decoder genuinely needs. Never dropped from a real .tif.
    var TIFF_STRUCTURAL = {};
    [0x00FE, 0x00FF, 0x0100, 0x0101, 0x0102, 0x0103, 0x0106, 0x0107, 0x0108, 0x0109,
     0x010A, 0x010D, 0x0111, 0x0112, 0x0115, 0x0116, 0x0117, 0x011A, 0x011B, 0x011C,
     0x0118, 0x0119, 0x0122, 0x0123, 0x0124, 0x0125, 0x0128, 0x012D, 0x013D,
     0x0140, 0x0141, 0x0142, 0x0143, 0x0144, 0x0145, 0x014A, 0x014C, 0x0150, 0x0151,
     0x0152, 0x0153, 0x0154, 0x0155, 0x0156, 0x0157, 0x0158, 0x015A, 0x015B,
     0x0200, 0x0201, 0x0202, 0x0211, 0x0212, 0x0213, 0x0214, 0x828D, 0x828E,
     0x013E, 0x013F, 0x83BC].forEach(function (t) { TIFF_STRUCTURAL[t] = true; });

    // Parse one IFD chain. Returns {entries, next} with value bytes materialised.
    function parseIfd(u8, base, off, le, depth, seen) {
        if (depth > 6 || off < 0 || base + off + 2 > u8.length) return null;
        seen = seen || {};
        if (seen[off]) return null;          // malformed self-referencing IFD
        seen[off] = 1;
        var count = rd(u8, base + off, 2, le);
        if (count > 4096) return null;       // junk guard
        var entries = [], p = base + off + 2, i;
        for (i = 0; i < count; i++, p += 12) {
            if (p + 12 > u8.length) break;
            var tag = rd(u8, p, 2, le);
            var type = rd(u8, p + 2, 2, le);
            var cnt = rd(u8, p + 4, 4, le);
            var ts = TYPE_SIZE[type];
            if (!ts) continue;                             // unknown type → drop
            var bytes = ts * cnt;
            if (bytes > u8.length) continue;               // junk guard
            var val;
            if (bytes <= 4) {
                val = u8.slice(p + 8, p + 8 + bytes);
            } else {
                var vo = rd(u8, p + 8, 4, le);
                if (base + vo + bytes > u8.length) continue;
                val = u8.slice(base + vo, base + vo + bytes);
            }
            var e = { tag: tag, type: type, count: cnt, val: val, sub: null };
            if (tag === T.ExifIFD || tag === T.GPSIFD || tag === T.InteropIFD) {
                var so = rd(val, 0, 4, le);
                e.sub = parseIfd(u8, base, so, le, depth + 1, seen);
            }
            entries.push(e);
        }
        var nextOff = 0;
        if (p + 4 <= u8.length) nextOff = rd(u8, p, 4, le);
        return { entries: entries, next: nextOff };
    }

    // Read a whole TIFF/EXIF block into {le, ifd0, ifd1, thumb}.
    function parseTiffBlock(u8) {
        if (u8.length < 8) return null;
        var le;
        if (u8[0] === 0x49 && u8[1] === 0x49) le = true;
        else if (u8[0] === 0x4D && u8[1] === 0x4D) le = false;
        else return null;
        if (rd(u8, 2, 2, le) !== 42) return null;
        var off0 = rd(u8, 4, 4, le);
        var ifd0 = parseIfd(u8, 0, off0, le, 0);
        if (!ifd0) return null;
        var ifd1 = null, thumb = null;
        if (ifd0.next) {
            ifd1 = parseIfd(u8, 0, ifd0.next, le, 0);
            if (ifd1) {
                var to = null, tl = null;
                ifd1.entries.forEach(function (e) {
                    if (e.tag === T.JPEGInterchangeFormat) to = rd(e.val, 0, 4, le);
                    if (e.tag === T.JPEGInterchangeFormatLength) tl = rd(e.val, 0, 4, le);
                });
                if (to != null && tl != null && to + tl <= u8.length) thumb = u8.slice(to, to + tl);
            }
        }
        return { le: le, ifd0: ifd0, ifd1: ifd1, thumb: thumb, raw: u8 };
    }

    // Classify an entry (which IFD it sits in changes the default).
    function classOf(tag, which) {
        if (TAG_CLASS[tag]) return TAG_CLASS[tag];
        if (which === 'gps') return 'gps';
        if (which === 'exif' || which === 'interop') return 'settings';
        return 'other';
    }

    /* Rebuild a TIFF block keeping only allowed entries.
     * `structural` = true when this is a real .tif whose pixel offsets must be
     * relocated (JPEG's EXIF block has no pixels of its own).             */
    function buildTiffBlock(parsed, keep, opt) {
        opt = opt || {};
        var le = parsed.le;
        var keepOrientation = opt.keepOrientation !== false;
        var structural = !!opt.structural;
        var pixelBlocks = [];    // {entryRef, offsets:[], counts:[]} for .tif strips

        function filterList(entries, which) {
            var out = [];
            for (var i = 0; i < entries.length; i++) {
                var e = entries[i], tag = e.tag;
                if (tag === T.ExifIFD || tag === T.GPSIFD || tag === T.InteropIFD) continue; // re-added below
                if (structural && TIFF_STRUCTURAL[tag] && !TAG_CLASS[tag]) { out.push(e); continue; }
                if (tag === T.Orientation) { if (keepOrientation) out.push(e); continue; }
                // resolution / colour-space plumbing is not identifying
                if (tag === T.XResolution || tag === T.YResolution || tag === T.ResolutionUnit ||
                    tag === T.YCbCrPositioning || tag === T.YCbCrCoefficients ||
                    tag === T.ReferenceBlackWhite || tag === T.WhitePoint ||
                    tag === T.PrimaryChromaticities) { out.push(e); continue; }
                var cls = classOf(tag, which);
                if (keep[cls]) out.push(e);
            }
            return out;
        }

        var e0 = filterList(parsed.ifd0.entries, 'ifd0');
        var exifSub = null, gpsSub = null, interopSub = null;
        parsed.ifd0.entries.forEach(function (e) {
            if (e.tag === T.ExifIFD && e.sub) exifSub = filterList(e.sub.entries, 'exif');
            if (e.tag === T.GPSIFD && e.sub && keep.gps) gpsSub = e.sub.entries.slice();
            if (e.tag === T.InteropIFD && e.sub) interopSub = filterList(e.sub.entries, 'interop');
        });
        if (exifSub && !exifSub.length) exifSub = null;
        if (gpsSub && !gpsSub.length) gpsSub = null;
        if (interopSub && !interopSub.length) interopSub = null;

        var e1 = null, thumb = null;
        if (keep.thumbnail && parsed.ifd1 && parsed.thumb) {
            e1 = filterList(parsed.ifd1.entries, 'ifd0').filter(function (e) {
                return e.tag !== T.JPEGInterchangeFormat && e.tag !== T.JPEGInterchangeFormatLength;
            });
            thumb = parsed.thumb;
        }

        // Nothing worth writing at all → caller drops the whole block.
        if (!e0.length && !exifSub && !gpsSub && !e1) return null;

        /* ─── layout pass ───────────────────────────────────────────────
         * Order: header │ IFD0 │ IFD0 values │ Exif │ vals │ GPS │ vals │
         *        Interop │ vals │ IFD1 │ vals │ thumbnail bytes
         * Every entry >4 bytes needs an out-of-line slot; we place them
         * directly after their own IFD so offsets stay monotonic (some
         * strict parsers care).                                          */
        function ifdSize(list, extraPointers) {
            var n = list.length + (extraPointers || 0);
            var size = 2 + n * 12 + 4, i;
            for (i = 0; i < list.length; i++) {
                var b = list[i].val.length;
                if (b > 4) size += b + (b & 1);   // word-align
            }
            return size;
        }
        var ptr0 = (exifSub ? 1 : 0) + (gpsSub ? 1 : 0);
        var size0 = ifdSize(e0, ptr0);
        var sizeExif = exifSub ? ifdSize(exifSub, interopSub ? 1 : 0) : 0;
        var sizeGps = gpsSub ? ifdSize(gpsSub, 0) : 0;
        var sizeInterop = interopSub ? ifdSize(interopSub, 0) : 0;
        var size1 = e1 ? ifdSize(e1, 2) : 0;

        var off0 = 8;
        var offExif = off0 + size0;
        var offGps = offExif + sizeExif;
        var offInterop = offGps + sizeGps;
        var off1 = offInterop + sizeInterop;
        var offThumb = off1 + size1;
        var total = offThumb + (thumb ? thumb.length : 0);

        var out = new Uint8Array(total);
        // header
        out[0] = le ? 0x49 : 0x4D; out[1] = le ? 0x49 : 0x4D;
        wr(out, 2, 2, 42, le);
        wr(out, 4, 4, off0, le);

        // Write one IFD. `pointers` = [{tag, target}] appended in tag order.
        function writeIfd(at, list, pointers, nextIfd, thumbInfo) {
            pointers = pointers || [];
            var all = list.slice();
            pointers.forEach(function (p) {
                all.push({ tag: p.tag, type: 4, count: 1, val: (function () {
                    var v = new Uint8Array(4); wr(v, 0, 4, p.target, le); return v;
                })() });
            });
            if (thumbInfo) {
                all.push({ tag: T.JPEGInterchangeFormat, type: 4, count: 1, val: (function () {
                    var v = new Uint8Array(4); wr(v, 0, 4, thumbInfo.off, le); return v;
                })() });
                all.push({ tag: T.JPEGInterchangeFormatLength, type: 4, count: 1, val: (function () {
                    var v = new Uint8Array(4); wr(v, 0, 4, thumbInfo.len, le); return v;
                })() });
            }
            all.sort(function (a, b) { return a.tag - b.tag; });   // TIFF requires ascending tags
            wr(out, at, 2, all.length, le);
            var p = at + 2;
            var valPos = at + 2 + all.length * 12 + 4;
            all.forEach(function (e) {
                wr(out, p, 2, e.tag, le);
                wr(out, p + 2, 2, e.type, le);
                wr(out, p + 4, 4, e.count, le);
                if (e.val.length <= 4) {
                    out.set(e.val, p + 8);
                    for (var q = e.val.length; q < 4; q++) out[p + 8 + q] = 0;
                } else {
                    wr(out, p + 8, 4, valPos, le);
                    out.set(e.val, valPos);
                    valPos += e.val.length + (e.val.length & 1);
                }
                p += 12;
            });
            wr(out, p, 4, nextIfd || 0, le);
        }

        var ptrs0 = [];
        if (exifSub) ptrs0.push({ tag: T.ExifIFD, target: offExif });
        if (gpsSub) ptrs0.push({ tag: T.GPSIFD, target: offGps });
        writeIfd(off0, e0, ptrs0, e1 ? off1 : 0, null);
        if (exifSub) writeIfd(offExif, exifSub, interopSub ? [{ tag: T.InteropIFD, target: offInterop }] : [], 0, null);
        if (gpsSub) writeIfd(offGps, gpsSub, [], 0, null);
        if (interopSub) writeIfd(offInterop, interopSub, [], 0, null);
        if (e1) writeIfd(off1, e1, [], 0, { off: offThumb, len: thumb.length });
        if (thumb) out.set(thumb, offThumb);
        return out;
    }

    // A 26-byte EXIF block that says nothing except "this photo is rotated".
    function orientationOnlyExif(orientation, le) {
        le = le !== false;
        var out = new Uint8Array(2 + 2 + 4 + 2 + 12 + 4);
        out[0] = le ? 0x49 : 0x4D; out[1] = le ? 0x49 : 0x4D;
        wr(out, 2, 2, 42, le);
        wr(out, 4, 4, 8, le);
        wr(out, 8, 2, 1, le);                    // one entry
        wr(out, 10, 2, T.Orientation, le);
        wr(out, 12, 2, 3, le);                   // SHORT
        wr(out, 14, 4, 1, le);
        wr(out, 18, 2, orientation, le);
        wr(out, 20, 2, 0, le);
        wr(out, 22, 4, 0, le);                   // no next IFD
        return out;
    }

    /* ─── human-readable findings from a TIFF block ─── */
    function decodeAscii(e) {
        var s = '';
        for (var i = 0; i < e.val.length; i++) { if (!e.val[i]) break; s += String.fromCharCode(e.val[i]); }
        return s.trim();
    }
    function ratio(e, le, i) {
        var o = i * 8;
        var n = rd(e.val, o, 4, le), d = rd(e.val, o + 4, 4, le);
        return d ? n / d : 0;
    }
    function gpsFrom(entries, le) {
        var lat = null, lon = null, latRef = 'N', lonRef = 'E', alt = null;
        entries.forEach(function (e) {
            if (e.tag === 0x0001) latRef = decodeAscii(e) || 'N';
            if (e.tag === 0x0002 && e.count >= 3) lat = ratio(e, le, 0) + ratio(e, le, 1) / 60 + ratio(e, le, 2) / 3600;
            if (e.tag === 0x0003) lonRef = decodeAscii(e) || 'E';
            if (e.tag === 0x0004 && e.count >= 3) lon = ratio(e, le, 0) + ratio(e, le, 1) / 60 + ratio(e, le, 2) / 3600;
            if (e.tag === 0x0006 && e.count >= 1) alt = ratio(e, le, 0);
        });
        if (lat == null || lon == null) return null;
        if (/S/i.test(latRef)) lat = -lat;
        if (/W/i.test(lonRef)) lon = -lon;
        return { lat: lat, lon: lon, alt: alt };
    }

    // Walk a parsed TIFF block and describe what a stranger could learn.
    function findingsFromTiff(parsed, add) {
        var le = parsed.le;
        var byClass = {};
        function note(cls, label, detail) {
            add(cls, label, detail);
            byClass[cls] = true;
        }
        var make = null, model = null;
        parsed.ifd0.entries.forEach(function (e) {
            if (e.tag === T.Make) make = decodeAscii(e);
            if (e.tag === T.Model) model = decodeAscii(e);
            if (e.tag === T.Artist) note('person', 'Artist', decodeAscii(e));
            if (e.tag === T.ImageDescription) note('person', 'ImageDescription', decodeAscii(e));
            if (e.tag === T.HostComputer) note('camera', 'HostComputer', decodeAscii(e));
            if (e.tag === T.Software) note('camera', 'Software', decodeAscii(e));
            if (e.tag === T.Copyright) note('copyright', 'Copyright', decodeAscii(e));
            if (e.tag === T.DateTime) note('datetime', 'DateTime', decodeAscii(e));
            if (e.tag === T.XPAuthor || e.tag === T.XPComment || e.tag === T.XPTitle ||
                e.tag === T.XPKeywords || e.tag === T.XPSubject) note('person', 'XP field', '');
            if (e.tag === T.XMLPacket) note('person', 'XMP', '');
            if (e.tag === T.IPTC) note('person', 'IPTC', '');
            if (e.tag === T.ExifIFD && e.sub) {
                e.sub.entries.forEach(function (x) {
                    if (x.tag === T.DateTimeOriginal) note('datetime', 'DateTimeOriginal', decodeAscii(x));
                    if (x.tag === T.LensModel) note('camera', 'LensModel', decodeAscii(x));
                    if (x.tag === T.BodySerialNumber) note('serial', 'BodySerialNumber', decodeAscii(x));
                    if (x.tag === T.LensSerialNumber) note('serial', 'LensSerialNumber', decodeAscii(x));
                    if (x.tag === T.CameraOwnerName) note('person', 'CameraOwnerName', decodeAscii(x));
                    if (x.tag === T.UserComment) note('person', 'UserComment', decodeAscii(x).replace(/^(ASCII|UNICODE)\s*/i, ''));
                    if (x.tag === T.MakerNote) note('serial', 'MakerNote', x.val.length + ' B');
                    if (x.tag === T.ImageUniqueID) note('serial', 'ImageUniqueID', decodeAscii(x));
                    if (!TAG_CLASS[x.tag]) byClass.settings = true;
                });
            }
            if (e.tag === T.GPSIFD && e.sub) {
                var g = gpsFrom(e.sub.entries, le);
                if (g) note('gps', 'GPS', g.lat.toFixed(5) + ', ' + g.lon.toFixed(5));
                else if (e.sub.entries.length) note('gps', 'GPS', '');
            }
        });
        if (make || model) note('camera', 'Camera', [make, model].filter(Boolean).join(' '));
        if (parsed.thumb) note('thumbnail', 'Embedded thumbnail', parsed.thumb.length + ' B');
        if (byClass.settings) add('settings', 'Shooting settings', '');
        return byClass;
    }

    /* ════════════════════════════════════════════════════════════════════
     * JPEG
     * ════════════════════════════════════════════════════════════════════ */
    // Walk the marker stream INCLUDING entropy-coded scan data, which is the
    // part naive walkers get wrong (they stop at the first SOS and then treat
    // everything after as opaque — that is exactly how a trailing MP4 slips
    // through). Returns segments plus the offset where the image truly ends.
    function walkJpeg(u8) {
        var segs = [], i = 2, n = u8.length, endOfImage = n, sawEoi = false;
        while (i < n - 1) {
            if (u8[i] !== 0xFF) { i++; continue; }
            var marker = u8[i + 1];
            if (marker === 0xFF) { i++; continue; }                    // fill byte
            if (marker === 0x00) { i += 2; continue; }                 // stuffed
            if (marker === 0xD9) { endOfImage = i + 2; sawEoi = true; break; }   // EOI — the image ends HERE
            if (marker >= 0xD0 && marker <= 0xD7 || marker === 0x01) { i += 2; continue; }
            if (i + 4 > n) break;
            var len = (u8[i + 2] << 8) | u8[i + 3];
            if (len < 2) break;
            var end = i + 2 + len;
            if (end > n) { end = n; }
            segs.push({ marker: marker, start: i, end: end, dataStart: i + 4, len: len });
            i = end;
            if (marker === 0xDA) {
                // entropy-coded data: skip until a real marker appears
                var j = end;
                while (j < n - 1) {
                    if (u8[j] !== 0xFF) { j++; continue; }
                    var m2 = u8[j + 1];
                    if (m2 === 0x00 || m2 === 0xFF || (m2 >= 0xD0 && m2 <= 0xD7)) { j += 2; continue; }
                    break;
                }
                i = j;
            }
        }
        // No EOI anywhere = the download or the copy was cut short. Worth
        // saying out loud: we will still clean it, but the user should know the
        // photo they are about to publish is incomplete.
        return { segs: segs, endOfImage: endOfImage, truncated: !sawEoi };
    }

    function segKind(u8, s) {
        var m = s.marker;
        if (m === 0xE0) return ascii(u8, s.dataStart, 5) === 'JFIF\0' || ascii(u8, s.dataStart, 5) === 'JFXX\0' ? 'jfif' : 'appn';
        if (m === 0xE1) {
            if (ascii(u8, s.dataStart, 6) === 'Exif\0\0') return 'exif';
            if (ascii(u8, s.dataStart, 28).indexOf('http://ns.adobe.com/xap') === 0) return 'xmp';
            return 'appn';
        }
        if (m === 0xE2) {
            if (ascii(u8, s.dataStart, 11) === 'ICC_PROFILE') return 'icc';
            if (ascii(u8, s.dataStart, 4) === 'MPF\0') return 'mpf';
            return 'appn';
        }
        if (m === 0xEB) return 'jumbf';                       // APP11 — C2PA lives here
        if (m === 0xED) return 'iptc';                        // APP13 — Photoshop/IPTC
        if (m === 0xEE) return ascii(u8, s.dataStart, 5) === 'Adobe' ? 'adobe' : 'appn';
        if (m === 0xFE) return 'comment';
        if (m >= 0xE3 && m <= 0xEF) return 'appn';
        return 'structural';
    }

    function scrubJpeg(u8, keep, o, report) {
        var w = walkJpeg(u8);
        if (w.truncated) report.damaged = true;
        var keepRanges = [[0, 2]];                            // SOI
        var exifReplacement = null;
        var orientation = 1;

        // Read the existing EXIF once: needed both for findings and to decide
        // what (if anything) replaces it.
        var exifSeg = null;
        w.segs.forEach(function (s) { if (!exifSeg && segKind(u8, s) === 'exif') exifSeg = s; });
        if (exifSeg) {
            var block = u8.subarray(exifSeg.dataStart + 6, exifSeg.end);
            var parsed = parseTiffBlock(block);
            if (parsed) {
                parsed.ifd0.entries.forEach(function (e) {
                    if (e.tag === T.Orientation) orientation = rd(e.val, 0, 2, parsed.le) || 1;
                });
                report._exif = parsed;
                var rebuilt = buildTiffBlock(parsed, keep, { keepOrientation: o.keepOrientation });
                if (rebuilt) exifReplacement = rebuilt;
            }
        }
        // No EXIF survives but the photo is rotated → keep orientation alone.
        if (!exifReplacement && o.keepOrientation && orientation > 1) {
            exifReplacement = orientationOnlyExif(orientation, true);
        }
        // Report it whichever route kept it: the UI tells the user "your photo
        // was sideways and still is", which is the difference between a tool
        // that looks broken and one that explains itself.
        if (o.keepOrientation && orientation > 1) report.orientationPreserved = orientation;

        var wroteExif = false;
        var parts = [];
        var lastEnd = 2;
        function flush(upTo) { if (upTo > lastEnd) keepRanges.push([lastEnd, upTo]); }

        // Rebuild by walking segments in order.
        var out = [u8.subarray(0, 2)];
        var prevEnd = 2;
        for (var k = 0; k < w.segs.length; k++) {
            var s = w.segs[k];
            // copy any bytes between segments verbatim (entropy data etc.)
            if (s.start > prevEnd) out.push(u8.subarray(prevEnd, s.start));
            var kind = segKind(u8, s);
            var drop = false;
            switch (kind) {
                case 'exif':
                    drop = true;
                    if (exifReplacement && !wroteExif) {
                        out.push(makeApp1Exif(exifReplacement));
                        wroteExif = true;
                    }
                    report._seen.exif = true;
                    break;
                case 'xmp':   drop = !keep.person; report._seen.xmp = true; break;
                case 'iptc':  drop = !keep.person; report._seen.iptc = true; break;
                case 'comment': drop = !keep.person; report._seen.comment = true; break;
                case 'jumbf': drop = !keep.c2pa; report._seen.c2pa = true; break;
                case 'mpf':   drop = !keep.hidden; report._seen.mpf = true; break;
                case 'icc':   drop = !keep.icc; report._seen.icc = true; break;
                case 'jfif':  drop = false; break;    // density info, harmless
                case 'adobe': drop = false; break;    // colour transform — load-bearing
                case 'appn':  drop = !keep.other; report._seen.other = true; break;
                default:      drop = false;           // DQT/DHT/SOF/SOS/DRI …
            }
            if (!drop) out.push(u8.subarray(s.start, s.end));
            prevEnd = s.end;
        }
        // Everything from the last segment to EOI is scan data — verbatim.
        if (w.endOfImage > prevEnd) out.push(u8.subarray(prevEnd, w.endOfImage));

        // If the file never had EXIF but is rotated (rare), insert after SOI.
        if (exifReplacement && !wroteExif) {
            out.splice(1, 0, makeApp1Exif(exifReplacement));
            wroteExif = true;
        }

        // ── THE TRAILING PAYLOAD ──
        // Bytes past EOI are not part of the image. Samsung/Google Motion
        // Photos put an entire MP4 there; MPF puts extra full-size JPEGs.
        var trailing = u8.length - w.endOfImage;
        if (trailing > 0) {
            report._seen.hidden = true;
            report.hiddenBytes = trailing;
            var tail = ascii(u8, w.endOfImage, Math.min(trailing, 4096));
            if (/MotionPhoto_Data|ftyp/.test(tail)) report.hiddenKind = 'video';
            if (keep.hidden) out.push(u8.subarray(w.endOfImage));
        }
        return concat(out);
    }

    function makeApp1Exif(block) {
        var len = block.length + 6 + 2;
        var head = new Uint8Array(4 + 6);
        head[0] = 0xFF; head[1] = 0xE1;
        head[2] = (len >> 8) & 0xff; head[3] = len & 0xff;
        head[4] = 0x45; head[5] = 0x78; head[6] = 0x69; head[7] = 0x66; head[8] = 0; head[9] = 0;
        return concat([head, block]);
    }

    /* ════════════════════════════════════════════════════════════════════
     * PNG  (incl. APNG — dropping acTL/fcTL/fdAT would kill the animation)
     * ════════════════════════════════════════════════════════════════════ */
    var PNG_KEEP = {
        IHDR: 1, PLTE: 1, IDAT: 1, IEND: 1, tRNS: 1, gAMA: 1, cHRM: 1, sRGB: 1,
        sBIT: 1, bKGD: 1, pHYs: 1, hIST: 1, sPLT: 1, cICP: 1, mDCv: 1, cLLi: 1,
        acTL: 1, fcTL: 1, fdAT: 1,                 // APNG animation
    };
    var PNG_META_CLASS = {
        tEXt: 'person', zTXt: 'person', iTXt: 'person', eXIf: 'exif', tIME: 'datetime',
        caBX: 'c2pa', dSIG: 'other', iCCP: 'icc',
    };
    function walkPngChunks(u8) {
        var chunks = [], i = 8;
        while (i + 8 <= u8.length) {
            var len = rd(u8, i, 4, false);
            var type = ascii(u8, i + 4, 4);
            var end = i + 12 + len;
            if (end > u8.length || len > u8.length) break;
            chunks.push({ type: type, start: i, end: end, dataStart: i + 8, dataLen: len });
            i = end;
            if (type === 'IEND') break;
        }
        return { chunks: chunks, endOfImage: i };
    }
    function scrubPng(u8, keep, o, report) {
        var w = walkPngChunks(u8);
        if (!w.chunks.some(function (c) { return c.type === 'IEND'; })) report.damaged = true;
        var out = [u8.subarray(0, 8)];
        w.chunks.forEach(function (c) {
            if (c.type === 'iCCP') { if (keep.icc) out.push(u8.subarray(c.start, c.end)); else report._seen.icc = true; return; }
            if (PNG_KEEP[c.type]) { out.push(u8.subarray(c.start, c.end)); return; }
            var cls = PNG_META_CLASS[c.type] || 'other';
            if (cls === 'exif') {
                report._seen.exif = true;
                var parsed = parseTiffBlock(u8.slice(c.dataStart, c.dataStart + c.dataLen));
                if (parsed) {
                    report._exif = parsed;
                    var rebuilt = buildTiffBlock(parsed, keep, { keepOrientation: o.keepOrientation });
                    if (rebuilt) { out.push(makePngChunk('eXIf', rebuilt)); return; }
                }
                return;
            }
            report._seen[cls === 'person' ? 'text' : cls] = true;
            if (keep[cls]) out.push(u8.subarray(c.start, c.end));
        });
        // Data appended after IEND (the classic "ZIP hidden in a PNG") is gone.
        var trailing = u8.length - w.endOfImage;
        if (trailing > 0) {
            report._seen.hidden = true;
            report.hiddenBytes = trailing;
            if (keep.hidden) out.push(u8.subarray(w.endOfImage));
        }
        return concat(out);
    }
    var CRC_TABLE = (function () {
        var t = new Uint32Array(256);
        for (var n = 0; n < 256; n++) { var c = n; for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1); t[n] = c >>> 0; }
        return t;
    })();
    function crc32(u8, start, end) {
        var c = 0xFFFFFFFF;
        for (var i = start; i < end; i++) c = CRC_TABLE[(c ^ u8[i]) & 0xFF] ^ (c >>> 8);
        return (c ^ 0xFFFFFFFF) >>> 0;
    }
    function makePngChunk(type, data) {
        var out = new Uint8Array(12 + data.length);
        wr(out, 0, 4, data.length, false);
        for (var i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
        out.set(data, 8);
        wr(out, 8 + data.length, 4, crc32(out, 4, 8 + data.length), false);
        return out;
    }

    /* ════════════════════════════════════════════════════════════════════
     * WebP (RIFF)
     * ════════════════════════════════════════════════════════════════════ */
    var WEBP_KEEP = { 'VP8 ': 1, 'VP8L': 1, 'VP8X': 1, 'ALPH': 1, 'ANIM': 1, 'ANMF': 1 };
    function scrubWebp(u8, keep, o, report) {
        var riffSize = rd(u8, 4, 4, true);
        var fileEnd = Math.min(u8.length, 8 + riffSize);
        var out = [u8.slice(0, 12)];
        var i = 12, vp8xAt = -1, pos = 12;
        while (i + 8 <= fileEnd) {
            var fourcc = ascii(u8, i, 4);
            var size = rd(u8, i + 4, 4, true);
            var padded = size + (size & 1);
            var end = i + 8 + padded;
            if (end > fileEnd) break;
            var drop = false;
            if (fourcc === 'EXIF') {
                report._seen.exif = true;
                var parsed = parseTiffBlock(u8.slice(i + 8, i + 8 + size));
                if (parsed) {
                    report._exif = parsed;
                    var rebuilt = buildTiffBlock(parsed, keep, { keepOrientation: o.keepOrientation });
                    if (rebuilt) {
                        out.push(makeRiffChunk('EXIF', rebuilt)); pos += 8 + rebuilt.length + (rebuilt.length & 1);
                        i = end; continue;
                    }
                }
                drop = true;
            } else if (fourcc === 'XMP ') { report._seen.xmp = true; drop = !keep.person; }
            else if (fourcc === 'ICCP') { report._seen.icc = true; drop = !keep.icc; }
            else if (!WEBP_KEEP[fourcc]) { report._seen.other = true; drop = !keep.other; }
            if (fourcc === 'VP8X') vp8xAt = pos;
            if (!drop) { out.push(u8.slice(i, end)); pos += end - i; }
            i = end;
        }
        var trailing = u8.length - fileEnd;
        if (trailing > 0) { report._seen.hidden = true; report.hiddenBytes = trailing; }
        var res = concat(out);
        wr(res, 4, 4, res.length - 8, true);
        // Clear the VP8X feature flags for chunks that no longer exist, or
        // decoders go looking for them.
        if (vp8xAt > 0 && vp8xAt + 8 < res.length) {
            if (!keep.icc) res[vp8xAt + 8] &= ~0x20;   // ICC
            res[vp8xAt + 8] &= ~0x08;                  // EXIF
            if (!keep.person) res[vp8xAt + 8] &= ~0x04; // XMP
        }
        return res;
    }
    function makeRiffChunk(fourcc, data) {
        var pad = data.length & 1;
        var out = new Uint8Array(8 + data.length + pad);
        for (var i = 0; i < 4; i++) out[i] = fourcc.charCodeAt(i);
        wr(out, 4, 4, data.length, true);
        out.set(data, 8);
        return out;
    }

    /* ════════════════════════════════════════════════════════════════════
     * GIF — comment / XMP blocks go, NETSCAPE loop control stays
     * (dropping every Application Extension is how tools break animation)
     * ════════════════════════════════════════════════════════════════════ */
    function scrubGif(u8, keep, o, report) {
        var i = 6;
        if (i + 7 > u8.length) return u8;
        var flags = u8[i + 4];
        var gctSize = (flags & 0x80) ? 3 * (1 << ((flags & 0x07) + 1)) : 0;
        i += 7 + gctSize;
        var out = [u8.subarray(0, i)];
        function skipSubBlocks(p) {
            while (p < u8.length) {
                var n = u8[p];
                if (n === 0) return p + 1;
                p += 1 + n;
            }
            return p;
        }
        while (i < u8.length) {
            var b = u8[i];
            if (b === 0x3B) { i += 1; out.push(new Uint8Array([0x3B])); break; }   // trailer
            if (b === 0x21) {
                var label = u8[i + 1];
                var start = i;
                var end = skipSubBlocks(i + 2);
                if (label === 0xF9) { out.push(u8.subarray(start, end)); }         // graphic control — timing
                else if (label === 0xFF) {
                    var appId = ascii(u8, i + 3, 11);
                    if (/NETSCAPE2\.0|ANIMEXTS1\.0/.test(appId)) out.push(u8.subarray(start, end));
                    else { report._seen[/XMP/i.test(appId) ? 'xmp' : 'other'] = true; if (keep.other) out.push(u8.subarray(start, end)); }
                } else if (label === 0xFE) { report._seen.comment = true; if (keep.person) out.push(u8.subarray(start, end)); }
                else { report._seen.other = true; if (keep.other) out.push(u8.subarray(start, end)); }
                i = end;
                continue;
            }
            if (b === 0x2C) {                                                       // image descriptor
                var lf = u8[i + 9];
                var lct = (lf & 0x80) ? 3 * (1 << ((lf & 0x07) + 1)) : 0;
                var p = i + 10 + lct + 1;                                           // + LZW min code size
                var e = skipSubBlocks(p);
                out.push(u8.subarray(i, e));
                i = e;
                continue;
            }
            break;
        }
        var trailing = u8.length - i;
        if (trailing > 0) { report._seen.hidden = true; report.hiddenBytes = trailing; }
        return concat(out);
    }

    /* ════════════════════════════════════════════════════════════════════
     * ISO-BMFF — HEIC / HEIF / AVIF  (iPhone's default photo format)
     * ────────────────────────────────────────────────────────────────────
     * Metadata lives as *items* declared in meta/iinf, located by meta/iloc,
     * with the payload sitting in mdat next to the pixels. Naively deleting
     * bytes from mdat would shift every other item's absolute offset and
     * corrupt the image, which is presumably why most tools simply refuse
     * HEIC. We keep the file length IDENTICAL:
     *   • rebuild meta without the offending item declarations, then pad the
     *     saved bytes back with a `free` box INSIDE meta (a standard filler
     *     every ISO-BMFF reader skips) — so mdat never moves;
     *   • zero the orphaned payload bytes in place, so the secret is not just
     *     unreferenced but physically gone.
     * ════════════════════════════════════════════════════════════════════ */
    function walkBoxes(u8, start, end, depth) {
        var boxes = [], i = start;
        while (i + 8 <= end) {
            var size = rd(u8, i, 4, false);
            var type = ascii(u8, i + 4, 4);
            var hdr = 8;
            if (size === 1) { if (i + 16 > end) break; size = rd(u8, i + 8, 8, false); hdr = 16; }
            else if (size === 0) size = end - i;
            if (size < hdr || i + size > end) break;
            boxes.push({ type: type, start: i, end: i + size, dataStart: i + hdr, size: size, hdr: hdr });
            i += size;
        }
        return boxes;
    }
    function parseIinf(u8, box) {
        // FullBox(4) + entry_count(2 or 4 depending on version)
        var v = u8[box.dataStart];
        var p = box.dataStart + 4;
        var count;
        if (v === 0) { count = rd(u8, p, 2, false); p += 2; }
        else { count = rd(u8, p, 4, false); p += 4; }
        var items = [], boxes = walkBoxes(u8, p, box.end, 0);
        boxes.forEach(function (b) {
            if (b.type !== 'infe') return;
            var ver = u8[b.dataStart];
            var q = b.dataStart + 4, id, type = '', name = '', contentType = '';
            if (ver >= 2) {
                if (ver === 2) { id = rd(u8, q, 2, false); q += 2; }
                else { id = rd(u8, q, 4, false); q += 4; }
                q += 2;                                   // protection index
                type = ascii(u8, q, 4); q += 4;
                var s = '';
                while (q < b.end && u8[q]) { s += String.fromCharCode(u8[q]); q++; }
                name = s; q++;
                if (type === 'mime') {
                    var ct = '';
                    while (q < b.end && u8[q]) { ct += String.fromCharCode(u8[q]); q++; }
                    contentType = ct;
                }
            } else { id = rd(u8, q, 2, false); }
            items.push({ id: id, type: type, name: name, contentType: contentType, box: b });
        });
        return { count: count, items: items, version: v };
    }
    function parseIloc(u8, box) {
        var v = u8[box.dataStart];
        var p = box.dataStart + 4;
        var b1 = u8[p], b2 = u8[p + 1];
        var offSize = b1 >> 4, lenSize = b1 & 15, baseSize = b2 >> 4;
        var indexSize = (v === 1 || v === 2) ? (b2 & 15) : 0;
        p += 2;
        var count;
        if (v < 2) { count = rd(u8, p, 2, false); p += 2; }
        else { count = rd(u8, p, 4, false); p += 4; }
        var items = [];
        for (var i = 0; i < count; i++) {
            var entryStart = p;
            var id;
            if (v < 2) { id = rd(u8, p, 2, false); p += 2; } else { id = rd(u8, p, 4, false); p += 4; }
            var construction = 0;
            if (v === 1 || v === 2) { construction = rd(u8, p, 2, false) & 15; p += 2; }
            p += 2;                                           // data_reference_index
            var base = baseSize ? rd(u8, p, baseSize, false) : 0; p += baseSize;
            var extCount = rd(u8, p, 2, false); p += 2;
            var extents = [];
            for (var e = 0; e < extCount; e++) {
                if (indexSize) p += indexSize;
                var off = offSize ? rd(u8, p, offSize, false) : 0; p += offSize;
                var lenAt = p;
                var len = lenSize ? rd(u8, p, lenSize, false) : 0; p += lenSize;
                extents.push({ offset: base + off, length: len, lenAt: lenAt, lenSize: lenSize, construction: construction });
            }
            items.push({ id: id, extents: extents, start: entryStart, end: p });
        }
        return { version: v, items: items, headerEnd: p, offSize: offSize, lenSize: lenSize, baseSize: baseSize, indexSize: indexSize };
    }

    function scrubBmff(u8, keep, o, report) {
        var top = walkBoxes(u8, 0, u8.length, 0);
        var out = new Uint8Array(u8);        // work on a copy; length preserved
        var metaBox = null;
        top.forEach(function (b) { if (b.type === 'meta') metaBox = b; });

        // Top-level uuid boxes (XMP is commonly stored this way) → `free`.
        top.forEach(function (b) {
            if (b.type !== 'uuid') return;
            report._seen.xmp = true;
            if (keep.person) return;
            for (var q = b.dataStart; q < b.end; q++) out[q] = 0;
            out[b.start + 4] = 0x66; out[b.start + 5] = 0x72;
            out[b.start + 6] = 0x65; out[b.start + 7] = 0x65;   // 'free'
        });

        if (!metaBox) return out;
        var metaChildren = walkBoxes(u8, metaBox.dataStart + 4, metaBox.end, 0);
        var iinfBox = null, ilocBox = null, irefBox = null;
        metaChildren.forEach(function (b) {
            if (b.type === 'iinf') iinfBox = b;
            if (b.type === 'iloc') ilocBox = b;
            if (b.type === 'iref') irefBox = b;
        });
        if (!iinfBox || !ilocBox) return out;

        var iinf = parseIinf(u8, iinfBox);
        var iloc = parseIloc(u8, ilocBox);
        var locById = {};
        iloc.items.forEach(function (it) { locById[it.id] = it; });

        // Which items are metadata (never the image itself)?
        var doomed = {};
        iinf.items.forEach(function (it) {
            var cls = null;
            if (it.type === 'Exif') cls = 'exif';
            else if (it.type === 'mime') cls = /xml|rdf|xmp/i.test(it.contentType) ? 'xmp' : 'other';
            else if (it.type === 'jumb' || it.type === 'c2pa') cls = 'c2pa';
            if (!cls) return;
            report._seen[cls] = true;
            // EXIF is special: it may be partly kept (orientation, settings…).
            if (cls === 'exif') {
                var loc = locById[it.id];
                if (loc && loc.extents.length) {
                    var ex = loc.extents[0];
                    // The Exif item payload is prefixed by a 4-byte offset to the TIFF header.
                    var s = ex.offset, l = ex.length;
                    if (s + l <= u8.length && l > 4) {
                        var skip = rd(u8, s, 4, false);
                        var tiffStart = s + 4 + skip;
                        if (tiffStart < s + l) {
                            var parsed = parseTiffBlock(u8.slice(tiffStart, s + l));
                            if (parsed) report._exif = parsed;
                        }
                    }
                }
                if (keep.gps || keep.camera || keep.serial || keep.datetime || keep.settings || keep.person || keep.copyright) {
                    // Selective keep inside HEIC would require re-laying out mdat.
                    // Honest behaviour: keep the item, note the limitation.
                    // NOT during the keep-everything probe: that pass keeps all
                    // classes by definition, so the note would fire for every
                    // HEIC ever inspected and the UI read it as "damaged file".
                    if (!o.probing) report.warnings.push('bmff_exif_all_or_nothing');
                    return;
                }
                doomed[it.id] = cls;
                return;
            }
            if (!keep[cls === 'xmp' ? 'person' : cls]) doomed[it.id] = cls;
        });

        var doomedIds = Object.keys(doomed).map(Number);
        if (!doomedIds.length) return out;

        // Payload ranges to erase. Collected now, applied LAST: the meta box is
        // written wholesale from the original bytes, so zeroing first would be
        // silently undone for any payload that lives inside meta/idat.
        var zeroRanges = [];
        doomedIds.forEach(function (id) {
            var loc = locById[id];
            if (!loc) return;
            loc.extents.forEach(function (ex) {
                zeroRanges.push([ex.offset, Math.min(u8.length, ex.offset + ex.length)]);
            });
        });
        function eraseAll() {
            zeroRanges.forEach(function (r) { for (var q = r[0]; q < r[1]; q++) out[q] = 0; });
        }

        // rebuild iinf / iref / iloc without them, padding meta back to size
        var newIinf = rebuildIinf(u8, iinfBox, iinf, doomed);
        var newIloc = rebuildIloc(u8, ilocBox, iloc, doomed);
        var newIref = irefBox ? rebuildIref(u8, irefBox, doomed) : null;

        var rebuilt = [];
        var delta = 0;
        metaChildren.forEach(function (b) {
            if (b === iinfBox) { rebuilt.push(newIinf); delta += b.size - newIinf.length; }
            else if (b === ilocBox) { rebuilt.push(newIloc); delta += b.size - newIloc.length; }
            else if (newIref && b === irefBox) { rebuilt.push(newIref); delta += b.size - newIref.length; }
            else rebuilt.push(u8.subarray(b.start, b.end));
        });

        if (delta === 0) { /* nothing to pad */ }
        else if (delta >= 8) {
            var free = new Uint8Array(delta);
            wr(free, 0, 4, delta, false);
            free[4] = 0x66; free[5] = 0x72; free[6] = 0x65; free[7] = 0x65;
            rebuilt.push(free);
        } else {
            // Cannot express <8 bytes as a box. Erasing the payload alone is
            // still a complete privacy fix, so take that rather than risk a
            // corrupt container for the sake of tidy bookkeeping.
            report.warnings.push('bmff_padding_skipped');
            eraseAll();
            return out;
        }

        var newMetaBody = concat(rebuilt);
        var expected = metaBox.end - (metaBox.dataStart + 4);
        if (newMetaBody.length !== expected) {
            report.warnings.push('bmff_size_mismatch');
            eraseAll();
            return out;
        }
        out.set(newMetaBody, metaBox.dataStart + 4);
        eraseAll();                                            // must come last
        return out;
    }

    function rebuildIinf(u8, box, iinf, doomed) {
        var kept = iinf.items.filter(function (it) { return !doomed[it.id]; });
        var head = copyOf(u8, box.start, box.dataStart + 4);    // box header + FullBox
        var cntBytes;
        if (iinf.version === 0) { cntBytes = new Uint8Array(2); wr(cntBytes, 0, 2, kept.length, false); }
        else { cntBytes = new Uint8Array(4); wr(cntBytes, 0, 4, kept.length, false); }
        var parts = [head, cntBytes];
        kept.forEach(function (it) { parts.push(u8.subarray(it.box.start, it.box.end)); });
        var res = concat(parts);
        wr(res, 0, 4, res.length, false);
        return res;
    }
    function rebuildIloc(u8, box, iloc, doomed) {
        var kept = iloc.items.filter(function (it) { return !doomed[it.id]; });
        var headEnd = iloc.items.length ? iloc.items[0].start : iloc.headerEnd;
        var head = copyOf(u8, box.start, headEnd);
        // patch item_count inside the copied header
        var v = iloc.version;
        var cntOffset = (box.dataStart + 4 + 2) - box.start;
        if (v < 2) wr(head, cntOffset, 2, kept.length, false);
        else wr(head, cntOffset, 4, kept.length, false);
        var parts = [head];
        kept.forEach(function (it) { parts.push(u8.subarray(it.start, it.end)); });
        var res = concat(parts);
        wr(res, 0, 4, res.length, false);
        return res;
    }
    function rebuildIref(u8, box, doomed) {
        var v = u8[box.dataStart];
        var children = walkBoxes(u8, box.dataStart + 4, box.end, 0);
        var parts = [copyOf(u8, box.start, box.dataStart + 4)];
        children.forEach(function (c) {
            var idSize = (v === 0) ? 2 : 4;
            var from = rd(u8, c.dataStart, idSize, false);
            if (doomed[from]) return;                            // reference from a removed item
            parts.push(u8.subarray(c.start, c.end));
        });
        var res = concat(parts);
        wr(res, 0, 4, res.length, false);
        return res;
    }

    /* ════════════════════════════════════════════════════════════════════
     * TIFF files — the IFD *is* the metadata, so rebuild it and relocate
     * the pixel strips.
     * ════════════════════════════════════════════════════════════════════ */
    function scrubTiff(u8, keep, o, report) {
        var parsed = parseTiffBlock(u8);
        if (!parsed) { report.warnings.push('tiff_parse_failed'); report.damaged = true; return u8; }
        report._exif = parsed;
        // Collect strip/tile data, rebuild the IFD, then append the pixels and
        // patch their offsets.
        var le = parsed.le;
        var e0 = parsed.ifd0.entries;
        var offTag = null, cntTag = null;
        e0.forEach(function (e) {
            if (e.tag === T.StripOffsets || e.tag === T.TileOffsets) offTag = e;
            if (e.tag === T.StripByteCounts || e.tag === T.TileByteCounts) cntTag = e;
        });
        var built = buildTiffBlock(parsed, keep, { keepOrientation: o.keepOrientation, structural: true });
        if (!built) { report.warnings.push('tiff_build_failed'); return u8; }
        if (!offTag || !cntTag) return built;

        // Extract the pixel blocks from the ORIGINAL and re-append them.
        var n = offTag.count, blocks = [], i;
        var osz = TYPE_SIZE[offTag.type], csz = TYPE_SIZE[cntTag.type];
        for (i = 0; i < n; i++) {
            var so = rd(offTag.val, i * osz, osz, le);
            var sc = rd(cntTag.val, i * csz, csz, le);
            if (so + sc > u8.length) { report.warnings.push('tiff_strip_out_of_range'); return u8; }
            blocks.push(u8.slice(so, so + sc));
        }
        var res = concat([built].concat(blocks));
        // patch the new offsets inside `res`
        var newParsed = parseTiffBlock(res);
        if (!newParsed) { report.warnings.push('tiff_rebuild_unreadable'); return u8; }
        // locate the StripOffsets entry position in the rebuilt buffer
        var pos = findEntryValuePos(res, newParsed, offTag.tag);
        if (pos == null) { report.warnings.push('tiff_offset_patch_failed'); return u8; }
        var cursor = built.length;
        for (i = 0; i < n; i++) {
            wr(res, pos + i * osz, osz, cursor, le);
            cursor += blocks[i].length;
        }
        return res;
    }
    // Where do this tag's VALUE bytes live in the serialized buffer?
    function findEntryValuePos(u8, parsed, tag) {
        var le = parsed.le;
        var off0 = rd(u8, 4, 4, le);
        var count = rd(u8, off0, 2, le);
        for (var i = 0; i < count; i++) {
            var p = off0 + 2 + i * 12;
            if (rd(u8, p, 2, le) !== tag) continue;
            var type = rd(u8, p + 2, 2, le);
            var cnt = rd(u8, p + 4, 4, le);
            var bytes = (TYPE_SIZE[type] || 1) * cnt;
            return bytes <= 4 ? p + 8 : rd(u8, p + 8, 4, le);
        }
        return null;
    }

    /* ════════════════════════════════════════════════════════════════════
     * INSPECT — what would a stranger learn from this file?
     * ════════════════════════════════════════════════════════════════════ */
    function newReport(format, size, name) {
        return {
            name: name || '', format: format, sizeIn: size, sizeOut: size,
            findings: [], classes: {}, removed: [], kept: [], warnings: [],
            gps: null, hiddenBytes: 0, hiddenKind: null, orientationPreserved: 0,
            /* `damaged` is a claim about the FILE; `warnings` is a note about
             * what the engine did. Conflating them told every HEIC user their
             * photo was corrupt, because a purely informational BMFF note sat
             * in the same list. The UI may only ever key on this flag. */
            damaged: false,
            _seen: {}, _exif: null,
        };
    }

    function collectFindings(u8, format, report) {
        function add(cls, label, detail) {
            report.classes[cls] = true;
            report.findings.push({ cls: cls, label: label, detail: detail || '' });
        }
        if (report._exif) {
            findingsFromTiff(report._exif, add);
            var g = null;
            report._exif.ifd0.entries.forEach(function (e) {
                if (e.tag === T.GPSIFD && e.sub) g = gpsFrom(e.sub.entries, report._exif.le);
            });
            if (g) report.gps = g;
        }
        var s = report._seen;
        if (s.xmp) add('person', 'XMP', '');
        if (s.iptc) add('person', 'IPTC', '');
        if (s.comment) add('person', 'Comment', '');
        if (s.text) add('person', 'Text chunks', '');
        if (s.c2pa) add('c2pa', 'C2PA Content Credentials', '');
        if (s.mpf) add('hidden', 'MPF (extra embedded images)', '');
        if (s.icc) add('icc', 'ICC profile', '');
        if (s.other) add('other', 'Other metadata', '');
        if (report.hiddenBytes > 0) {
            add('hidden', report.hiddenKind === 'video' ? 'Hidden video (Motion Photo)' : 'Hidden appended data',
                fmtBytes(report.hiddenBytes));
        }
        if (s.datetime) add('datetime', 'Timestamp', '');
    }
    function fmtBytes(n) {
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
        return (n / 1024 / 1024).toFixed(1) + ' MB';
    }

    /* ════════════════════════════════════════════════════════════════════
     * PUBLIC API
     * ════════════════════════════════════════════════════════════════════ */
    var SUPPORTED = { jpeg: 1, png: 1, webp: 1, gif: 1, heic: 1, avif: 1, tiff: 1 };

    /* Canonical extension for a DETECTED format.
     * Needed because the extension a file arrives with is frequently a lie:
     * messengers and cloud drives rename a PNG to .jpg all the time. We detect
     * by signature (correct), so we must also NAME by signature — otherwise the
     * cleaned copy inherits the same wrong extension and the user is no better
     * off than before. `current` keeps a legitimate spelling (.jpeg, .tif). */
    var CANONICAL_EXT = { jpeg: 'jpg', png: 'png', webp: 'webp', gif: 'gif',
                          heic: 'heic', avif: 'avif', tiff: 'tif' };
    // .heif is the umbrella spelling for the whole ISO-BMFF image family, so a
    // file the user gave us as .heif stays .heif whether the payload turns out
    // to be HEVC or AV1. Renaming it to .avif would be technically defensible
    // and practically unhelpful — it breaks whatever workflow named it.
    var EXT_ALIASES = { jpeg: ['jpg', 'jpeg'], tiff: ['tif', 'tiff'],
                        heic: ['heic', 'heif'], avif: ['avif', 'heif'] };
    // heic / heif / avif are one container family that differs only in codec.
    // Rewriting .heic → .avif would be pedantically true and practically
    // useless; the rename exists for a WRONG FAMILY (PNG bytes in a .jpg), not
    // for a different codec inside the same box structure.
    var BMFF_EXT = { heic: 1, heif: 1, avif: 1 };
    function canonicalExt(format, current) {
        var want = CANONICAL_EXT[format];
        if (!want) return current || '';
        var cur = current ? String(current).toLowerCase() : '';
        if (cur && BMFF_EXT[cur] && BMFF_EXT[want]) return cur;
        var ok = EXT_ALIASES[format] || [want];
        if (cur && ok.indexOf(cur) !== -1) return cur;
        return want;
    }

    // Read-only: never mutates, used for the "what's inside" panel.
    function inspect(bytes, name) {
        var u8 = u8of(bytes);
        var format = detect(u8);
        var report = newReport(format, u8.length, name);
        if (!SUPPORTED[format]) { report.warnings.push('unsupported'); return report; }
        // Run a scrub with everything kept: it walks the same code path and
        // fills report._seen, so inspection can never disagree with cleaning.
        try {
            var keepAll = {};
            CLASSES.forEach(function (c) { keepAll[c] = true; });
            runScrub(u8, format, keepAll, { keepOrientation: true, probing: true }, report);
        } catch (e) { report.warnings.push('inspect_failed'); }
        collectFindings(u8, format, report);
        return report;
    }

    function runScrub(u8, format, keep, o, report) {
        switch (format) {
            case 'jpeg': return scrubJpeg(u8, keep, o, report);
            case 'png': return scrubPng(u8, keep, o, report);
            case 'webp': return scrubWebp(u8, keep, o, report);
            case 'gif': return scrubGif(u8, keep, o, report);
            case 'heic': case 'avif': return scrubBmff(u8, keep, o, report);
            case 'tiff': return scrubTiff(u8, keep, o, report);
            default: return u8;
        }
    }

    /* scrub(bytes, {preset|keep, keepOrientation}) → {bytes, report}
     * Never throws on a supported format; unsupported input comes back
     * untouched with `report.warnings = ['unsupported']` so a batch run can
     * report the skip honestly instead of silently "succeeding". */
    function scrub(bytes, opts) {
        var u8 = u8of(bytes);
        var format = detect(u8);
        var report = newReport(format, u8.length, opts && opts.name);
        if (!SUPPORTED[format]) {
            report.warnings.push('unsupported');
            report.bytes = u8;
            return { bytes: u8, report: report };
        }
        var o = defaults(opts && opts.keep ? opts : { keep: presetKeep((opts && opts.preset) || 'full'), keepOrientation: opts && opts.keepOrientation });
        /* "What was there" needs a keep-everything pass. Callers that already
         * ran inspect() (the page does, at intake) can hand that report back in
         * as opts.probe — otherwise a batch pays for the same walk twice, and
         * with the verification re-read that made FOUR passes per file. */
        var probe = (opts && opts.probe && opts.probe.format === format) ? opts.probe : null;
        if (!probe) {
            probe = newReport(format, u8.length, opts && opts.name);
            try {
                var keepAll = {};
                CLASSES.forEach(function (c) { keepAll[c] = true; });
                runScrub(u8, format, keepAll, { keepOrientation: true, probing: true }, probe);
            } catch (e) {}
        }
        report._seen = probe._seen;
        report._exif = probe._exif;
        report.hiddenBytes = probe.hiddenBytes;
        report.hiddenKind = probe.hiddenKind;
        report.damaged = !!probe.damaged;
        collectFindings(u8, format, report);

        var out;
        try {
            var report2 = report;
            out = runScrub(u8, format, o.keep, o, report2);
        } catch (e) {
            report.warnings.push('scrub_failed:' + (e && e.message ? e.message : 'error'));
            return { bytes: u8, report: report };
        }
        report.sizeOut = out.length;
        // What actually went, expressed in the same vocabulary as the findings.
        var seenClasses = Object.keys(report.classes);
        seenClasses.forEach(function (c) {
            if (o.keep[c]) report.kept.push(c); else report.removed.push(c);
        });
        report.bytes = out;
        return { bytes: out, report: report };
    }

    return {
        detect: detect, inspect: inspect, scrub: scrub,
        SUPPORTED: SUPPORTED, CLASSES: CLASSES, PRESETS: PRESETS, presetKeep: presetKeep,
        fmtBytes: fmtBytes, canonicalExt: canonicalExt,
        // low-level, exported for the test harness
        walkJpeg: walkJpeg, walkPngChunks: walkPngChunks, walkBoxes: walkBoxes,
        parseTiffBlock: parseTiffBlock, buildTiffBlock: buildTiffBlock,
        orientationOnlyExif: orientationOnlyExif,
    };
});
